import groupBy from 'lodash.groupby';
import moment from 'moment';
import { LAB_SAMPLE_STATUSES } from '@/config/constants';
import {
  analysesApi,
  analysesDataApi,
  samplesApi,
  thermostatsApi,
} from '@/api';

/* eslint-disable no-param-reassign */
// STEP 1
const searchThermostat = async (component) => {
  if (component.thermostat.code) {
    component.formData.thermostatId = null;
    component.thermostat.name = null;
    component.thermostat.temperature = null;
    try {
      const thermostatRemote = await thermostatsApi.getByName(
        component.thermostat.code
      );
      if (thermostatRemote.data.length > 0) {
        const { id, name, enabled, currentTemperature } =
          thermostatRemote.data[0];
        if (enabled && currentTemperature) {
          component.formData.thermostatId = id;
          component.thermostat.name = name;
          component.thermostat.temperature = currentTemperature.value;
        } else if (enabled && !currentTemperature) {
          component.$notify({
            showClose: true,
            type: 'error',
            message: component.$t(
              'analysesData.valuesTemperatureBulk.noTemperatureErr'
            ),
          });
        } else if (!enabled) {
          component.$notify({
            showClose: true,
            type: 'error',
            message: component.$t(
              'analysesData.valuesTemperatureBulk.notEnabledErr'
            ),
          });
        }
      } else {
        component.$notify({
          showClose: true,
          type: 'error',
          message: component.$t('analysesData.valuesTemperatureBulk.notFound'),
        });
      }
    } catch (error) {
      component.$notify({
        showClose: true,
        type: 'error',
        message: error.response.data.error.message,
      });
    }
  }
  component.thermostat.code = null; // clears code when finished
};

// STEP 2
const searchGrouping = async (component) => {
  if (component.grouping.code) {
    try {
      const groupingSamples = await samplesApi.getSamplesFromGroupingId(
        component.grouping.code
      );
      if (groupingSamples.data.length > 0) {
        component.sampleIdsWithGrouping = {
          ...component.sampleIdsWithGrouping,
          [component.grouping.code]: groupingSamples.data,
        };
      } else {
        component.$notify({
          showClose: true,
          type: 'error',
          message: component.$t('analysesData.valuesTemperatureBulk.noSamples'),
        });
      }
    } catch (error) {
      component.$notify({
        showClose: true,
        type: 'error',
        message: error.response.data.error.message,
      });
    }
  }
  component.grouping.code = null;
};

const removeGrouping = (component, row) => {
  const { [row.groupingId]: removedGrouping, ...otherData } =
    component.sampleIdsWithGrouping;
  component.sampleIdsWithGrouping = { ...otherData };
};

// STEP 3
const getSampleAnalysesGrouped = async (component) => {
  if (component.sampleIdsFlattened.length > 0) {
    component.loading = true;
    try {
      const sampleAnalysesRemote =
        await analysesDataApi.samplesAnalysesNewIndex(
          component.sampleIdsFlattened
        );
      // group by analysis id
      const grouped = groupBy(
        sampleAnalysesRemote.data,
        (item) => item.analysisId
      );
      // create analysis list
      const processed = Object.keys(grouped)
        .sort()
        .map((key) => {
          const sampleAnalysesList = grouped[key];
          const sampleAnalysis = sampleAnalysesList[0]; // Used for analysis code and description

          return {
            analysisCode: sampleAnalysis.analysisCode,
            analysisDescription: sampleAnalysis.analysisDescription,
            analysisId: sampleAnalysis.analysisId,
            operations: [],
            sampleAnalysesList,
          };
        });
      component.sampleAnalyses = processed;
      component.loading = false;
    } catch (error) {
      component.loading = false;
      component.$notify({
        showClose: true,
        type: 'error',
        message: error.response.data.error.message,
      });
    }
  }
};

const analysesToOperationValues = async (
  sampleAnalysesList,
  labSamplesMappedByLabSampleId,
  operations,
  additionalData
) => {
  const sampleAnalysisOperationValues = sampleAnalysesList.flatMap(
    (sampleAnalysis) => {
      const { labSampleAnalysisId, labSampleId, analysisCode } = sampleAnalysis;
      const labSample = labSamplesMappedByLabSampleId[labSampleId];
      const labSampleCode = labSample ? labSample.labSampleCode : 'ERROR';
      // Only printOnWorksheet operations can have data CMPLF-341
      // CMPLF-627: filter by symbolType
      // const validOperations = operations.filter((op) => op.symbolType);
      const sampleAnalysisOperationMap = new Map(
        operations.map((operation) => [
          operation.id,
          {
            labSampleId,
            labSampleCode,
            labSampleAnalysisId,
            analysisCode,
            operationId: operation.id,
            operation,
            ...additionalData,
            valueId: undefined,
          },
        ])
      );

      // Add valueId to operationMap
      sampleAnalysis.values.forEach((value) => {
        if (sampleAnalysisOperationMap.has(value.operationId)) {
          sampleAnalysisOperationMap.get(value.operationId).valueId =
            value.sampleAnalysisOperationValueId;
        }
      });

      return [...sampleAnalysisOperationMap.values()];
    }
  );

  return Promise.all(
    sampleAnalysisOperationValues.map(async (sampleAnalysisOperationValue) => {
      if (sampleAnalysisOperationValue.valueId) {
        // fetch data
        const currentValue = await samplesApi.getSampleOperationValues(
          sampleAnalysisOperationValue.valueId
        );
        return {
          ...sampleAnalysisOperationValue,
          value: currentValue.data,
          symbol: currentValue.data.symbol,
          symbolLot: currentValue.data.symbolLot,
        };
      }
      return { ...sampleAnalysisOperationValue, value: null };
    })
  );
};

// NOTE: Pure function to find analysis by code
const findAnalysisByCode = (sampleAnalyses, analysisCode) => {
  return sampleAnalyses.find(
    (analysis) => analysis.analysisCode === analysisCode
  );
};

// NOTE: Pure function to create lab samples mapping
const createLabSamplesMappingById = (sampleIdsWithGrouping) => {
  return Object.values(sampleIdsWithGrouping)
    .flat()
    .reduce((obj, labSample) => {
      obj[labSample.labSampleId] = labSample;
      return obj;
    }, {});
};

// NOTE: Pure function to check for errors in values
const hasErrorsInValues = (values, ignoreEmptyValues = false) => {
  return ignoreEmptyValues
    ? false
    : values.some((value) => value.labSampleCode === 'ERROR');
};

// NOTE: Pure function to create analysis data structure
const createAnalysisData = (analysis, values) => ({
  code: analysis.analysisCode,
  description: analysis.analysisDescription,
  values,
});

// NOTE: Pure function to filter valid lab sample analyses
const filterValidLabSampleAnalyses = (
  sampleAnalysesList,
  labSamplesMappedByLabSampleId
) => {
  const validStatuses = [
    LAB_SAMPLE_STATUSES.ACCEPTED,
    LAB_SAMPLE_STATUSES.INPROGRESS,
    LAB_SAMPLE_STATUSES.TOBEVERIFIED,
  ];

  return sampleAnalysesList
    .map((a) => ({
      labSampleId: a.labSampleId,
      labSampleAnalysisId: a.labSampleAnalysisId,
      analysisId: a.analysisId,
      analysisCode: a.analysisCode,
      sampleValues: labSamplesMappedByLabSampleId[a.labSampleId],
    }))
    .filter(
      (v) => v.sampleValues && validStatuses.includes(v.sampleValues.status)
    );
};

// NOTE: Separated function to compute lab sample analysis IDs
const computeLabSampleAnalysisIds = (
  selectedAnalyses,
  sampleAnalyses,
  sampleIdsWithGrouping
) => {
  const labSamplesMappedByLabSampleId = createLabSamplesMappingById(
    sampleIdsWithGrouping
  );

  // NOTE: Get analysis codes from selectedAnalyses
  const selectedAnalysisCodes = Object.values(selectedAnalyses).map(
    (analysis) => analysis.code
  );

  return selectedAnalysisCodes
    .map((code) => findAnalysisByCode(sampleAnalyses, code))
    .filter((analysis) => analysis) // cleaning
    .flatMap((analysis) =>
      filterValidLabSampleAnalyses(
        analysis.sampleAnalysesList,
        labSamplesMappedByLabSampleId
      )
    )
    .reduce((acc, item) => {
      const isDuplicate = acc.find(
        (val) => val.labSampleAnalysisId === item.labSampleAnalysisId
      );
      return isDuplicate ? acc : [...acc, item];
    }, []);
};

// NOTE: Main search and select analysis function (focused responsibility)
const searchAndSelectAnalysis = async (
  analysisCode,
  sampleAnalyses,
  sampleIdsWithGrouping,
  additionalData,
  ignoreEmptyValues = false
) => {
  const analysis = findAnalysisByCode(sampleAnalyses, analysisCode);

  if (!analysis) {
    throw new Error('ANALYSIS_NOT_FOUND');
  }

  const operations = await analysesApi.operationsInAnalysis(
    analysis.analysisId
  );
  const labSamplesMappedByLabSampleId = createLabSamplesMappingById(
    sampleIdsWithGrouping
  );

  const values = await analysesToOperationValues(
    analysis.sampleAnalysesList,
    labSamplesMappedByLabSampleId,
    operations.data,
    additionalData
  );

  if (hasErrorsInValues(values, ignoreEmptyValues)) {
    throw new Error('VALUES_ERROR');
  }

  return {
    analysisId: analysis.analysisId,
    analysisData: createAnalysisData(analysis, values),
  };
};

// NOTE: Error handling
const handleAnalysisError = (component, error) => {
  let messageKey = 'analysesData.valuesTemperatureBulk.genericError';

  if (error.message === 'ANALYSIS_NOT_FOUND') {
    messageKey = 'analysesData.valuesTemperatureBulk.noAnalysis';
  } else if (error.message === 'VALUES_ERROR') {
    messageKey = 'analysesData.valuesTemperatureBulk.noValuesInAnalysis';
  } else if (error.response?.data?.error?.message) {
    // Use the original error message for API errors
    component.$notify({
      showClose: true,
      type: 'error',
      message: error.response.data.error.message,
    });
    return;
  }

  component.$notify({
    showClose: true,
    type: 'error',
    message: component.$t(messageKey),
  });
};

// NOTE: Main search function (focused on search and selection only)
const searchAnalysis = async (
  component,
  additionalData,
  ignoreEmptyValues = false
) => {
  if (!component.analysis.code) {
    return;
  }

  try {
    const result = await searchAndSelectAnalysis(
      component.analysis.code,
      component.sampleAnalyses,
      component.sampleIdsWithGrouping,
      additionalData,
      ignoreEmptyValues
    );

    // NOTE: Update selected analyses
    component.selectedAnalyses = {
      ...component.selectedAnalyses,
      [result.analysisId]: result.analysisData,
    };
  } catch (error) {
    handleAnalysisError(component, error);
  } finally {
    // NOTE: Clear the input after processing (success or failure)
    component.analysis.code = null;
  }
};

// NOTE: Separate function to recompute all lab sample analysis IDs
const recomputeLabSampleAnalysisIds = (component) => {
  // This can be called independently whenever needed
  component.labSampleAnalysisIds = computeLabSampleAnalysisIds(
    component.selectedAnalyses,
    component.sampleAnalyses,
    component.sampleIdsWithGrouping
  );
};

const removeAnalysis = (component, row) => {
  const { [row.id]: removedAnalysis, ...otherData } =
    component.selectedAnalyses;
  component.selectedAnalyses = { ...otherData };
  component.labSampleAnalysisIds = component.labSampleAnalysisIds.filter(
    (val) => val.analysisCode !== row.code
  );
};

// STEP 4
const getTemperatureTrails = async (component) => {
  const { labSampleAnalysisIds } = component;
  if (!labSampleAnalysisIds) {
    component.temperatureTrails = [];
    return;
  }
  try {
    const labSampleAnalysisIdArray = labSampleAnalysisIds
      .map((analysis) => {
        return analysis.labSampleAnalysisId;
      })
      .filter((e) => e != null);
    const trails = await thermostatsApi.getTemperatureTrail({
      where: {
        labSampleAnalysisId: { inq: labSampleAnalysisIdArray },
        endedAt: null,
      },
    });
    const trailsByLabSample =
      await thermostatsApi.getTemperatureTrailByLabSample({
        labSampleAnalysisIds: labSampleAnalysisIdArray.join(','),
      });
    component.temperatureTrails = trails.data;
    component.temperatureTrailsByLabSample = trailsByLabSample.data;
    /* 
    This check seems to be overly strict, removed as for SMP-97 -->
    if (!component.checkTemperatureTrails()) {
      component.$notify({
        showClose: true,
        type: 'error',
        message: component.$t(
          'analysesData.valuesTemperatureBulk.invalidTrails'
        ),
      });
      component.prevStep();
    }
    */
    // Closing: the start comes from the trail and bounds the end the user entered
    const startDate = component.temperatureTrails[0]?.startedAt;
    if (startDate) {
      component.traceDateTime.startDateTime = new Date(startDate);
      const { endDateTime } = component.traceDateTime;
      if (endDateTime && moment(endDateTime).isBefore(startDate)) {
        component.traceDateTime.endDateTime = null;
      }
    }
  } catch (error) {
    if (error.response)
      component.$notify({
        showClose: true,
        type: 'error',
        message: error.response.data.error.message,
      });
  }
};

// const removeSampleFromSelectedGrouping = (component, row) => {
//   const { labSampleId, groupingId } = row;
//   const filteredGrouping = component.sampleIdsWithGrouping[groupingId].filter(
//     (s) => s.id !== labSampleId
//   );
//   component.sampleIdsWithGrouping = {
//     ...component.sampleIdsWithGrouping,
//     [groupingId]: filteredGrouping,
//   };
// };

// const getOperationValuesGrouped = (component) => {
//   const operationValues = Object.keys(component.selectedAnalyses)
//     .flatMap((key) => component.selectedAnalyses[key].values)
//     .filter((operation) => operation.symbolLot !== '');
//   component.operationValuesGrouped = groupBy(
//     operationValues,
//     (ov) => ov.operationId
//   );
// };

/* eslint-enable no-param-reassign */

export default (component) => ({
  searchThermostat: () => searchThermostat(component),
  searchGrouping: () => searchGrouping(component),
  removeGrouping: (row) => removeGrouping(component, row),
  getSampleAnalysesGrouped: () => getSampleAnalysesGrouped(component),
  searchAnalysis: (additionalData, ignoreEmptyValues) =>
    searchAnalysis(component, additionalData, ignoreEmptyValues),
  removeAnalysis: (row) => removeAnalysis(component, row),
  getTemperatureTrails: () => getTemperatureTrails(component),
  recomputeLabSampleAnalysisIds: () => recomputeLabSampleAnalysisIds(component),
  // removeSampleFromSelectedGrouping: (row) =>
  //   removeSampleFromSelectedGrouping(component, row),
  // getOperationValuesGrouped: () => getOperationValuesGrouped(component),
});
